import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from 'Components/Atoms/Button/Button';
import PropTypes, { func } from 'prop-types';
import { useQueryClient } from '@tanstack/react-query';
import useInvoiceLogo from 'Hooks/useInvoiceLogo';
import useFileUrl from 'Hooks/useFileUrl';
import InputFile from 'Components/Atoms/InputFile/InputFile';
import { updateInvoiceLogo, deleteInvoiceLogo } from 'Services/invoices';
import IconButton from 'Components/Atoms/IconButton/IconButton';
import Icon from 'Components/Atoms/Icon/Icon';

function ImagePreview({ src, onDelete }) {
    return (
        <div className="relative">
            <div className="relative h-20 p-2 flex justify-center items-center rounded-md border border-gray-200 bg-white">
                <img src={src} alt="" className="object-contain h-full w-auto" />
            </div>

            <div className="absolute top-0 right-0">
                <IconButton icon={<Icon name="trash" />} iconSize={20} variant="ghost" onClick={onDelete} />
            </div>
        </div>
    );
}

ImagePreview.propTypes = {
    src: PropTypes.string.isRequired,
    onDelete: func.isRequired,
};

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2 MB

export default function InvoicesInvoicingDefaultValuesDrawer(props) {
    const { onClose, onSubmit, onError } = props;

    const { t } = useTranslation('invoices');
    const [processing, setProcessing] = useState(false);
    const queryClient = useQueryClient();
    const { isLoading, invoiceLogo } = useInvoiceLogo();
    const [uploadFile, setUploadFile] = useState(null);
    const [hasDeletedLogo, setHasDeletedLogo] = useState(false);

    const uploadFileUrl = useFileUrl(uploadFile);

    const submit = async () => {
        setProcessing(true);

        try {
            if (hasDeletedLogo) {
                await deleteInvoiceLogo();
            }

            if (uploadFile) {
                await updateInvoiceLogo(uploadFile);
            }

            await queryClient.invalidateQueries({ queryKey: ['invoice-logo'] });
            onSubmit();
        } catch (error) {
            onError(error);
        } finally {
            setProcessing(false);
        }
    };

    const showUploadFile = (!invoiceLogo || hasDeletedLogo) && !uploadFileUrl;
    const showExistingLogo = invoiceLogo && !hasDeletedLogo && !uploadFileUrl;

    return (
        <div className="flex flex-col h-full">
            <div className="grow mt-8">
                <div className="uppercase font-semibold text-2xs tracking-widest mb-2 text-gray-500">
                    {t('company-logo')}
                </div>

                {showExistingLogo && <ImagePreview src={invoiceLogo} onDelete={() => setHasDeletedLogo(true)} />}
                {uploadFileUrl && <ImagePreview src={uploadFileUrl} onDelete={() => setUploadFile(null)} />}

                {showUploadFile && (
                    <InputFile
                        label={t('drop-logo-here')}
                        acceptedFileTypes="image/png,image/jpg,image/jpeg,image/svg+xml"
                        value={uploadFile}
                        isBusy={isLoading}
                        maxFileSize={MAX_FILE_SIZE}
                        onUpload={setUploadFile}
                        errorMessages={{
                            fileTooLarge: t('logo-file-too-large', { size: '2 MB' }),
                        }}
                    />
                )}
            </div>
            <div className="flex gap-2 py-5">
                <Button variant="primary" onClick={submit} isBusy={processing} type="button" className="grow">
                    {t('save')}
                </Button>
                <Button className="grow" onClick={onClose}>
                    {t('cancel')}
                </Button>
            </div>
        </div>
    );
}

InvoicesInvoicingDefaultValuesDrawer.propTypes = {
    onClose: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    onError: PropTypes.func.isRequired,
};
